import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { publications } from '../data/publications';

function FadeIn({ children, delay = 0 }) {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current; if (!el) return;
        const obs = new IntersectionObserver(([e]) => {
            if (e.isIntersecting) { el.classList.add('visible'); obs.disconnect(); }
        }, { threshold: 0.06 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    return <div ref={ref} className="fade-in" style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

function StatusBadge({ status }) {
    const map = {
        'Presented': { color: '#22C55E' },
        'Accepted': { color: '#3B82F6' },
        'Under Review': { color: '#F59E0B' },
        'Preprint': { color: '#8B5CF6' },
        'Published': { color: 'var(--accent)' }
    };
    const { color } = map[status] || { color: 'var(--text-muted)' };
    return (
        <span className="pub-status" style={{ '--sc': color }}>
            <span className="pub-status-dot" />
            {status}
        </span>
    );
}

function PublicationCard({ pub, delay = 0 }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <FadeIn delay={delay}>
            <div className="pub-card" id={`pub-${pub.id}`}>
                <div className="pub-top">
                    <StatusBadge status={pub.status} />
                    <div className="pub-venue-row">
                        <span className="pub-venue">{pub.venue}</span>
                        <span className="pub-venue-type">{pub.venueType}</span>
                        <span className="pub-sep">·</span>
                        <span className="pub-date">{pub.date}</span>
                        {pub.location && <>
                            <span className="pub-sep">·</span>
                            <span className="pub-date">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 10c0 6-8 13-8 13s-8-7-8-13a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>
                                {pub.location}
                            </span>
                        </>}
                    </div>
                </div>

                <h3 className="pub-title">{pub.title}</h3>

                <p className="pub-authors">
                    {pub.authors.map((a, i) => (
                        <span key={i}>
                            {a.includes('Lo Sardo') ? <strong>{a}</strong> : a}
                            {i < pub.authors.length - 1 ? ', ' : ''}
                        </span>
                    ))}
                </p>

                <div className={`pub-abstract ${expanded ? 'pub-abstract--expanded' : ''}`}>
                    <p>{pub.abstract}</p>
                </div>
                {pub.abstract.length > 180 && (
                    <button className="pub-expand" onClick={() => setExpanded(e => !e)}>
                        {expanded ? 'Show less ↑' : 'Read abstract ↓'}
                    </button>
                )}

                <div className="pub-footer">
                    <div className="tag-row">
                        {(pub.tags || []).map(t => <span key={t} className="tag">{t}</span>)}
                    </div>
                    {pub.link && (
                        <a href={pub.link} className="project-link" target="_blank" rel="noreferrer">
                            View Publication ↗
                        </a>
                    )}
                    {pub.pdfLink && !pub.link && (
                        <a href={pub.pdfLink} className="project-link" target="_blank" rel="noreferrer">
                            View PDF ↗
                        </a>
                    )}
                </div>
            </div>
        </FadeIn>
    );
}

export default function Publications() {
    const { hash } = useLocation();

    useEffect(() => {
        if (!hash) return;
        const id = hash.replace('#', '');
        setTimeout(() => {
            const el = document.getElementById(`pub-${id}`) || document.getElementById(id);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('pub-highlight');
                setTimeout(() => el.classList.remove('pub-highlight'), 2000);
            }
        }, 300);
    }, [hash]);

    return (
        <>
        <Helmet>
            <title>Publications | Salvatore Lo Sardo</title>
            <meta name="description" content="Research papers and conference presentations by Salvatore Lo Sardo — sentiment analysis, LLMs, economic NLP, and AI." />
            <meta property="og:title" content="Publications | Salvatore Lo Sardo" />
            <meta property="og:description" content="Research papers and conference presentations — sentiment analysis, LLMs, economic NLP, and AI." />
            <meta property="og:url" content="https://www.salvatore.losardo.eu/publications" />
            <link rel="canonical" href="https://www.salvatore.losardo.eu/publications" />
        </Helmet>
        <div className="page">
            <div className="container">
                <FadeIn>
                    <div className="page-hero page-hero--split">
                        <div>
                            <h1 className="page-hero-title">Publications</h1>
                            <p className="page-hero-sub">My academic publications, research papers, and conference presentations</p>
                        </div>
                        <a href="https://scholar.google.com/citations?user=TzWJ0VQAAAAJ&hl=en" target="_blank" rel="noreferrer" className="contact-btn contact-btn--secondary page-hero-action">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M5.242 13.769L0 9.5 12 0l12 9.5-5.242 4.269C17.548 11.249 14.978 9.5 12 9.5c-2.977 0-5.548 1.748-6.758 4.269zM12 10a7 7 0 1 0 0 14 7 7 0 0 0 0-14z" /></svg>
                            Google Scholar
                        </a>
                    </div>
                </FadeIn>

                {publications.length === 0 ? (
                    <FadeIn>
                        <div className="empty-state">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>
                            <p>No publications yet</p>
                            <p className="empty-sub">Check back soon</p>
                        </div>
                    </FadeIn>
                ) : (
                    <div className="pub-list">
                        {publications.map((pub, i) => (
                            <PublicationCard key={pub.id || i} pub={pub} delay={i * 60} />
                        ))}
                    </div>
                )}
            </div>
        </div>
        </>
    );
}
